import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components'
import { useAppSelector } from '../hooks'
import { DEFAULT_WORKSPACE_SETTINGS } from '../config/defaultSettings'
import type { WorkspaceSettings } from '../core/models/Workspace'
import { resolveAppearanceFromWorkspace, resolveThemeTokensFromWorkspace } from './resolveWorkspaceTheme'
import { applySevvCssVariables } from './applySevvCssVariables'
import { buildSevvMuiTheme } from './buildSevvTheme'

/**
 * Subscribes to workspace settings and applies the same tokens to MUI, styled-components, and :root CSS variables.
 */
export function SevvDynamicThemeRoot({ children }: { children: React.ReactNode }) {
  const settings = useAppSelector((s) => s.workspace.settings)
  const effective: WorkspaceSettings = settings ?? DEFAULT_WORKSPACE_SETTINGS

  const [prefersDark, setPrefersDark] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const sync = () => setPrefersDark(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  const appearance = useMemo(() => resolveAppearanceFromWorkspace(effective), [effective])
  const tokens = useMemo(() => resolveThemeTokensFromWorkspace(effective), [effective])

  const muiTheme = useMemo(
    () => buildSevvMuiTheme(tokens, appearance, prefersDark),
    [tokens, appearance, prefersDark]
  )

  useLayoutEffect(() => {
    applySevvCssVariables(document.documentElement, tokens, appearance, prefersDark)
  }, [tokens, appearance, prefersDark])

  return (
    <ThemeProvider theme={muiTheme}>
      <StyledComponentsThemeProvider theme={muiTheme}>{children}</StyledComponentsThemeProvider>
    </ThemeProvider>
  )
}
